import { Link } from "react-router-dom";
import { Handshake, Building, Users, GraduationCap, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const tracks = [
  {
    icon: Handshake,
    title: "Prime Subcontracting",
    desc: "Teaming with primes to deliver on logistics, transportation and real estate scopes — while helping primes meet their small business and diversity goals.",
  },
  {
    icon: Building,
    title: "Municipal & Agency Alliances",
    desc: "Long-horizon partnerships with cities, counties and agencies focused on infrastructure, freight movement and workforce development.",
  },
  {
    icon: Users,
    title: "Community & Workforce",
    desc: "Local hiring, training and reinvestment programs that translate every contract into tangible community impact.",
  },
  {
    icon: GraduationCap,
    title: "Institutional & Educational",
    desc: "Strategic collaborations with universities, trade institutions and nonprofits driving the next generation of operational talent.",
  },
];

export default function Partnerships() {
  useSEO({
    title: "Partnerships — Tandem T Holdings",
    description: "Strategic partnerships with primes, municipalities, agencies, and community institutions. Tandem T Holdings LLC — minority woman-owned holding company.",
    path: "/partnerships",
  });
  return (
    <div data-testid="page-partnerships">
      <PageHero
        testId="partnerships-hero"
        overline="Strategic Partnerships"
        title={<>Aligned partners. <em className="italic">Compounded outcomes.</em></>}
        subtitle="Tandem T Holdings partners with primes, agencies, municipalities and community institutions — turning shared ambition into measurable economic, operational and social returns."
        image="https://images.unsplash.com/photo-1717525930976-067585c1ceb3"
      />

      {/* TRACKS */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Partnership Tracks</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight">
              Four lanes of mutual leverage.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {tracks.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.title} delay={i * 0.06} className="bg-black">
                  <div className="p-10 md:p-14 min-h-[280px] hover:bg-white/[0.03] transition-colors">
                    <Icon size={22} className="text-white" />
                    <h3 className="font-serif font-light text-white text-3xl mt-8">{t.title}</h3>
                    <p className="text-white/65 font-light leading-relaxed mt-4 max-w-md">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUES BLOCK */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl leading-tight">
              Built on integrity.<br />
              <em className="italic">Operated with rigor.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-7">
            <p className="text-white/70 font-light leading-relaxed text-lg">
              Through integrity, strategic partnerships and operational excellence, we empower organizations
              to achieve their goals while building lasting value for future generations. The right partner
              changes the trajectory of an enterprise — we intend to be that partner.
            </p>
            <p className="font-serif italic text-white/55 mt-8 text-xl">
              "Partnership is not a transaction. It is the architecture of legacy."
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <h3 className="font-serif font-light text-white text-3xl md:text-5xl max-w-2xl leading-tight">
              Considering Tandem T as a partner or subcontractor?
            </h3>
          </Reveal>
          <Reveal delay={0.08}>
            <Link
              data-testid="partnerships-cta-contact"
              to="/contact?intent=partnership"
              className="group inline-flex items-center gap-3 px-6 py-4 bg-white text-black overline hover:bg-white/85"
            >
              Open a Conversation <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
